/** @jsx isolateComponent */

/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useState } from 'react';
import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';

import { css, keyframes } from '@emotion/react';

import { Animation, Button, VisuallyHidden } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';

import MenuCollapseButton from './MenuCollapseButton';
import type { UniversalTopbarProps } from './UniversalTopbar';
import UniversalTopbar from './UniversalTopbar';
import TopbarLoadingState from './TopbarLoadingState';
import { SIDEBAR_PRODUCT_MENU } from './UniversalTopbar/constants';
import UniversalSidebar, {
  UniversalSidebarContainer,
} from './UniversalSidebar';

import { addParamsToUrl, extractParams } from '@zapier/url-utils';
import {
  useUniversalNavContext,
  shouldRenderTopbarLinksForRoute,
} from '../hooks';

import { emitUserInteractionEvent } from '../utils/avroEvents';
import findAccountsMenuRenderer from '../utils/findAccountsMenuRenderer';
import useUnscrollableBody from '../hooks/useUnscrollableBody';
import { UNIVERSAL_NAV_ZINDEX, loginPath, signupPath } from '../constants';

import type { CallToAction, UniversalNavLinkType } from '../constants';

const MY_ZAPS_TEXT = 'My Zaps';
const LOGIN_TEXT = 'Log in';
const SIGN_UP_TEXT = 'Sign up';

const slideAnimation = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(0); }
`;

const Styles = labelStyles('UniversalNav', {
  root: ({
    topbarHeight,
    shouldHeaderStickToTop,
  }: {
    topbarHeight: number;
    shouldHeaderStickToTop: boolean;
  }) => [
    css`
      position: sticky;
      z-index: ${UNIVERSAL_NAV_ZINDEX};
      top: ${shouldHeaderStickToTop ? 0 : `-${topbarHeight}px`};
    `,
  ],

  sidebarCtaButtons: css`
    display: grid;
    grid-template-columns: 1fr fr;
    gap: 10px;
  `,

  forceMenuCollapseButton: css`
    margin-left: 5px;
  `,

  sidebarWrapper: ({
    isFixed,
    isFullWidth,
  }: {
    isFixed: boolean;
    isFullWidth: boolean;
  }) => [
    css`
      position: absolute;
      display: flex;
      align-items: stretch;
      // Push beneath UniversalTopbar border so the border is seen.
      margin-top: 1px;
      top: 100%;
      left: 0;
      height: 100vh;
      will-change: height;
      transition: height 300ms linear;
    `,

    isFixed &&
      !isFullWidth &&
      css`
        position: fixed;
        top: 0;
        bottom: 0;
        height: auto;
      `,

    isFullWidth &&
      css`
        // Pull above UniversalTopbar border so the border is hidden.
        margin-top: 0;
        width: 100%;
      `,

    !isFullWidth &&
      css`
        animation: ${slideAnimation} ${Animation.transitionDuration} ease-in-out;
      `,
  ],
});

export type Props = {
  /**
   * Renders notifications component.
   */
  renderNotifications: () => ReactNode;
  /**
   * Anchor link to the main content area on the page to allow
   * screenreaders to skip to the main content.
   */
  skipLinkHref?: string;
};

type SidebarProps = {
  callToAction?: CallToAction;
  currentAccountId?: number;
  currentUrl: string;
  height: string | number;
  isFixed: boolean;
  isLoggedIn: boolean;
  isOnAppPage: boolean;
  isSidebarControlledByTopbar: boolean;
  isSidebarForcedOpen: boolean;
  links: Array<UniversalNavLinkType>;
  loginHref: string;
  onClick?: (e: ReactMouseEvent) => void;
  shouldRenderTopbarLinks: boolean;
  signupHref: string;
  size: 'small' | 'medium';
  skipLinkHref?: string;
};

// Copies query strings from given source to destination
const copyQueryString = (source: string, destination: string) =>
  addParamsToUrl(destination, extractParams(source), true);

const Sidebar = (props: SidebarProps) => {
  const {
    getFullyQualifiedURL,
    shouldRenderAccessibleSidebar,
  } = useUniversalNavContext();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isFullWidth = props.size === 'small';
  const isActuallyCollapsed =
    !isFullWidth &&
    !props.isSidebarForcedOpen &&
    !props.isSidebarControlledByTopbar &&
    isCollapsed;
  const sharedProps = {
    isCollapsed: isActuallyCollapsed,
    isFullWidth,
    isShadowed:
      !isFullWidth && !props.isSidebarForcedOpen && !isActuallyCollapsed,
    onClick: props.onClick,
    onMouseEnter: () => {
      if (window.matchMedia('(hover: hover)').matches) {
        setIsCollapsed(false);
      }
    },
    onMouseLeave: () => {
      if (window.matchMedia('(hover: hover)').matches) {
        setIsCollapsed(true);
      }
    },
    selectedHref: props.currentUrl,
    skipLinkHref: props.skipLinkHref,
  };

  const shouldRenderLoggedInSidebar =
    props.isLoggedIn &&
    props.currentAccountId &&
    (props.isOnAppPage || props.shouldRenderTopbarLinks);

  useUnscrollableBody({
    shouldAllowScroll:
      (!isFullWidth && !sharedProps.isShadowed) ||
      shouldRenderAccessibleSidebar,
  });

  const callToActionUrl =
    getFullyQualifiedURL(
      `${props.callToAction?.href}?demo_source=cs_nav_header_${props.currentUrl}`
    ) || '';

  const handleCallToActionClick = (text?: string) => {
    emitUserInteractionEvent({
      event_action: 'click',
      option_selected: `UniversalDynamicTopbar: ${text}`,
    });
  };
  const ZAPS_URL = getFullyQualifiedURL('/app/zaps');
  const LOGIN_URL = getFullyQualifiedURL(props.loginHref);
  const SIGNUP_URL = getFullyQualifiedURL(props.signupHref);

  const sidebar = shouldRenderLoggedInSidebar ? (
    <UniversalSidebarContainer
      {...sharedProps}
      currentAccountId={props.currentAccountId!}
    />
  ) : (
    <UniversalSidebar
      {...sharedProps}
      links={props.links}
      logoAriaLabel="Go to Zapier homepage"
      logoHref={getFullyQualifiedURL('/')}
      renderFooter={() => {
        if (props.size === 'small' && !isActuallyCollapsed) {
          if (!props.isLoggedIn) {
            return (
              <div css={Styles.sidebarCtaButtons}>
                {props.callToAction && (
                  <Button
                    color="secondary"
                    href={callToActionUrl}
                    onClick={() =>
                      handleCallToActionClick(
                        `${props.callToAction?.text} to ${callToActionUrl}`
                      )
                    }
                    size="small"
                  >
                    {props.callToAction.text}
                  </Button>
                )}
                <Button
                  color="secondary"
                  href={LOGIN_URL}
                  size="small"
                  onClick={() =>
                    handleCallToActionClick(`${LOGIN_TEXT} ${LOGIN_URL}`)
                  }
                >
                  {LOGIN_TEXT}
                </Button>
                <Button
                  color="primary"
                  href={SIGNUP_URL}
                  size="small"
                  onClick={() =>
                    handleCallToActionClick(
                      `${SIGN_UP_TEXT} ${SIGNUP_URL}
                      )}`
                    )
                  }
                >
                  {SIGN_UP_TEXT}
                </Button>
              </div>
            );
          }
          if (props.isLoggedIn && !props.isOnAppPage) {
            return (
              <div css={Styles.sidebarCtaButtons}>
                {props.callToAction && (
                  <Button
                    color="secondary"
                    href={callToActionUrl}
                    isFullWidth={true}
                    size="medium"
                    onClick={() =>
                      handleCallToActionClick(
                        `${props.callToAction?.text} to ${callToActionUrl}`
                      )
                    }
                  >
                    {props.callToAction.text}
                  </Button>
                )}
                <Button
                  color="primary-alt"
                  href={ZAPS_URL}
                  isFullWidth={true}
                  size="medium"
                  onClick={() =>
                    handleCallToActionClick(`${MY_ZAPS_TEXT} ${ZAPS_URL}`)
                  }
                >
                  {MY_ZAPS_TEXT}
                </Button>
              </div>
            );
          }
        }
        return null;
      }}
    />
  );

  const style = { height: props.height };
  return (
    <div
      css={Styles.sidebarWrapper({
        isFixed: props.isFixed,
        isFullWidth,
      })}
      style={style}
    >
      {sidebar}
    </div>
  );
};

/**
 * Top-level universal navigation component that renders the
 * top bar and the sidebar.
 */
export const UniversalNav = (props: Props) => {
  const {
    canTopbarFitBackToAppButton,
    canForceSidebarOpen,
    currentAccountId,
    currentUrl,
    isLoadingLoggedIn,
    isLoadingTopNav,
    isLoggedIn,
    isOnAppPage,
    isSearchFullWidth,
    isSidebarForcedOpen,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    mode,
    onToggleSidebarVisibility,
    setIsSidebarForcedOpen,
    setIsSidebarOpenedByTopbar,
    setIsSidebarRendered,
    shouldRenderCallToAction,
    shouldRenderSearch,
    shouldRenderSidebar,
    shouldRenderAccessibleSidebar,
    shouldRenderTopbarLinks,
    topbarHeight,
    topbarSize,
    topNavData,
  } = useUniversalNavContext();

  const shouldHeaderStickToTop = shouldRenderTopbarLinksForRoute({
    currentUrl,
    mode,
  });

  useEffect(() => {
    setIsSidebarRendered(shouldRenderSidebar);
    return () => {
      setIsSidebarRendered(false);
    };
  }, [setIsSidebarRendered, shouldRenderSidebar]);

  const getLinksToRender = () => {
    if (isLoadingTopNav) {
      // Render an empty link. This will take up the space
      // in the main navigation content.
      // Avoids a shift in the logo
      return [{ text: '', href: '' }];
    }

    return topNavData.topBarMenu;
  };

  const linksToRender = getLinksToRender();

  const loginHref = copyQueryString(currentUrl, loginPath);
  const signupHref = copyQueryString(currentUrl, signupPath);

  const renderAccountMenu = findAccountsMenuRenderer({
    canTopbarFitBackToAppButton,
    currentAccountId,
    currentUrl,
    isLoadingLoggedIn,
    isLoggedIn,
    loginHref,
    signupHref,
  });

  // Use `undefined` to leverage default value, noop to prevent rendering.
  const renderSearchForm = shouldRenderSearch ? undefined : () => undefined;

  const [bottom, setBottom] = useState(Infinity);
  const navRef = useRef(null);
  useEffect(() => {
    const setNavBottom = () => {
      if (navRef.current) {
        setBottom(
          (navRef.current! as HTMLDivElement)!.getBoundingClientRect().bottom
        );
      }
    };
    setNavBottom();
    document.addEventListener('scroll', setNavBottom, { capture: true });
    return () => {
      document.removeEventListener('scroll', setNavBottom, { capture: true });
    };
  }, [topbarHeight, setBottom]);

  const closeSidebar = () => {
    setIsSidebarOpenedByTopbar(false);
  };

  const closeSidebarOnTriggerClick = (
    e: ReactMouseEvent | MouseEvent | TouchEvent
  ) => {
    const target = e!.target! as HTMLElement;
    const trigger = target.closest('a, button, label, [role="button"]');
    // Close the sidebar if any interactive node is clicked
    // and the sidebar is controlled by the topbar.
    if (isSidebarOpenedByTopbar && !!trigger) {
      closeSidebar();
    }
  };

  const renderSidebar = (topbarProps: UniversalTopbarProps) => {
    const buttonLabel = isSidebarForcedOpen ? 'Close Sidebar' : 'Open Sidebar';
    const buttonIcon = isSidebarForcedOpen ? 'formX' : 'navMenu';

    const toggleSidebarVisibilityButton = (() => {
      if (onToggleSidebarVisibility) {
        return (
          <MenuCollapseButton
            ariaExpanded={isSidebarOpenedByTopbar}
            icon={isSidebarOpenedByTopbar ? 'formX' : 'navMenu'}
            label="Menu"
            onClick={onToggleSidebarVisibility}
          />
        );
      }
      if (canForceSidebarOpen) {
        return (
          <div css={Styles.forceMenuCollapseButton}>
            <MenuCollapseButton
              ariaExpanded={isSidebarForcedOpen}
              icon={buttonIcon}
              label={buttonLabel}
              onClick={() => setIsSidebarForcedOpen(!isSidebarForcedOpen)}
            />
          </div>
        );
      }
      return null;
    })();

    let sidebarLinksToRender = linksToRender || [];
    sidebarLinksToRender = [SIDEBAR_PRODUCT_MENU, ...sidebarLinksToRender];

    const sidebar = shouldRenderSidebar && (
      <Sidebar
        callToAction={
          shouldRenderCallToAction ? topNavData.callToAction : undefined
        }
        currentAccountId={currentAccountId}
        currentUrl={currentUrl}
        height={`calc(100vh - ${Math.max(0, bottom)}px)`}
        isFixed={bottom <= 0}
        isLoggedIn={isLoggedIn}
        isOnAppPage={isOnAppPage}
        isSidebarControlledByTopbar={isSidebarControlledByTopbar}
        isSidebarForcedOpen={isSidebarForcedOpen}
        links={sidebarLinksToRender}
        loginHref={loginHref}
        onClick={closeSidebarOnTriggerClick}
        shouldRenderTopbarLinks={shouldRenderTopbarLinks}
        signupHref={signupHref}
        size={topbarProps.size}
        skipLinkHref={props.skipLinkHref}
      />
    );

    const accessibleSidebar = shouldRenderAccessibleSidebar && (
      <VisuallyHidden>
        <div data-testid="hidden-sidebar">
          <Sidebar
            currentAccountId={currentAccountId}
            currentUrl={currentUrl}
            height={`calc(100vh - ${Math.max(0, bottom)}px)`}
            isFixed={bottom <= 0}
            isLoggedIn={isLoggedIn}
            isOnAppPage={isOnAppPage}
            isSidebarControlledByTopbar={isSidebarControlledByTopbar}
            isSidebarForcedOpen={isSidebarForcedOpen}
            links={sidebarLinksToRender || []}
            loginHref={loginHref}
            onClick={closeSidebarOnTriggerClick}
            shouldRenderTopbarLinks={shouldRenderTopbarLinks}
            signupHref={signupHref}
            size={topbarProps.size}
            skipLinkHref={props.skipLinkHref}
          />
        </div>
      </VisuallyHidden>
    );
    // Need to return the nodes only if at least one exists,
    // otherwise the empty `Fragment` will cause a slot to
    // render when it shouldn't.
    return (
      (toggleSidebarVisibilityButton || sidebar) && (
        <React.Fragment>
          {toggleSidebarVisibilityButton}
          {sidebar}
          {accessibleSidebar}
        </React.Fragment>
      )
    );
  };

  // Track mounted state to force a rerender.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  return (
    <div
      css={Styles.root({ topbarHeight, shouldHeaderStickToTop })}
      key={hasMounted ? 'has-mounted' : 'has-not-mounted'}
      ref={navRef}
    >
      {isLoadingTopNav ? (
        <TopbarLoadingState
          height={topbarHeight}
          isSearchFullWidth={isSearchFullWidth}
          links={shouldRenderTopbarLinks ? linksToRender : []}
          onClick={closeSidebarOnTriggerClick}
          onClickOutside={closeSidebar}
          renderAccountMenu={renderAccountMenu}
          renderSearchForm={renderSearchForm}
          renderSidebar={renderSidebar}
          selectedHref={currentUrl}
          size={topbarSize}
          skipLinkHref={props.skipLinkHref}
        />
      ) : (
        <UniversalTopbar
          height={topbarHeight}
          isSearchFullWidth={isSearchFullWidth}
          links={shouldRenderTopbarLinks ? linksToRender : []}
          onClick={closeSidebarOnTriggerClick}
          onClickOutside={closeSidebar}
          renderAccountMenu={renderAccountMenu}
          renderSearchForm={renderSearchForm}
          renderSidebar={renderSidebar}
          selectedHref={currentUrl}
          size={topbarSize}
          skipLinkHref={props.skipLinkHref}
        />
      )}
    </div>
  );
};

UniversalNav.defaultProps = {
  renderNotifications: () => null,
};
